import React, {useEffect, useState} from 'react';
import {View, FlatList, StyleSheet, Image} from 'react-native';
import {Text, Card, Button, Appbar, ActivityIndicator, Snackbar} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import {colors, spacing} from '../../theme';

const MeasurementProfileSelectorScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {customerId, catalogItemId} = route.params;

  const {customerProfiles, fetchCustomerProfiles, isLoading} = useMeasurementStore();
  const [loaded, setLoaded] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        await fetchCustomerProfiles(customerId, catalogItemId);
      } catch (e) {
        setSnackbar(e.response?.data?.message || t('common.error'));
      } finally {
        setLoaded(true);
      }
    };
    load();
  }, [customerId, catalogItemId, fetchCustomerProfiles, t]);

  useEffect(() => {
    if (loaded && !isLoading && customerProfiles.length === 0) {
      navigation.replace('MeasurementForm', {
        customerId,
        catalogItemId,
        returnTo: 'OrderLineDetail',
        mode: 'create',
      });
    }
  }, [loaded, isLoading, customerProfiles, customerId, catalogItemId, navigation]);

  const handleSelect = (profile) => {
    navigation.push('MeasurementForm', {
      customerId,
      catalogItemId,
      profileId: profile.id,
      originalProfileName: profile.profileName,
      returnTo: 'OrderLineDetail',
      mode: 'edit',
    });
  };

  const handleCreateNew = () => {
    navigation.push('MeasurementForm', {
      customerId,
      catalogItemId,
      returnTo: 'OrderLineDetail',
      mode: 'create',
    });
  };

  if (!loaded || isLoading) {
    return (
      <View style={styles.container}>
        <Appbar.Header style={styles.appbar} dark>
          <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
          <Appbar.Content title="Select Measurement" titleStyle={styles.appbarTitle} />
        </Appbar.Header>
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (customerProfiles.length === 0) {
    return null;
  }

  const renderProfile = ({item}) => {
    const valueCount = item.values?.length || 0;
    const date = item.measuredAt || item.createdAt?.split('T')[0] || '';
    return (
      <Card style={styles.card} onPress={() => handleSelect(item)}>
        <Card.Content style={styles.cardContent}>
          {item.catalogItemImageUrl ? (
            <Image source={{uri: item.catalogItemImageUrl}} style={styles.catalogImage} />
          ) : (
            <View style={styles.catalogImageFallback}>
              <Text style={styles.catalogImageLetter}>
                {(item.catalogItemName || item.profileName || '?').charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <View style={styles.profileInfo}>
            <Text variant="titleMedium" style={styles.profileName}>{item.profileName}</Text>
            <Text variant="bodySmall" style={styles.profileMeta}>
              {date}{valueCount > 0 ? ` \u00B7 ${valueCount} measurements` : ''}
            </Text>
          </View>
        </Card.Content>
      </Card>
    );
  };

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title="Select Measurement" titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <FlatList
        data={customerProfiles}
        keyExtractor={item => String(item.id)}
        renderItem={renderProfile}
        contentContainerStyle={styles.list}
        ListFooterComponent={
          <Button
            mode="outlined"
            icon="plus"
            onPress={handleCreateNew}
            style={styles.createBtn}
            textColor={colors.primary}>
            Create New Measurement
          </Button>
        }
      />

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  centered: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  list: {padding: spacing.md},
  card: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  catalogImage: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: colors.border,
    marginRight: spacing.md,
  },
  catalogImageFallback: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#F0EBF8',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  catalogImageLetter: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.primary,
  },
  profileInfo: {flex: 1},
  profileName: {fontWeight: '600', color: colors.text},
  profileMeta: {color: colors.textSecondary, marginTop: spacing.xs},
  createBtn: {
    marginTop: spacing.md,
    marginBottom: spacing.xxl,
    borderColor: colors.primary,
    borderRadius: 8,
  },
});

export default MeasurementProfileSelectorScreen;
